import React, { useState, useEffect } from 'react';
import type { Question, Language } from '@nordcrest/shared';

interface RankingProps {
  question: Question;
  value: string[];
  onChange: (value: string[]) => void;
  language: Language;
  error?: string;
}

export function Ranking({ question, value, onChange, language, error }: RankingProps) {
  // Initialize with all options if not yet ranked
  const [rankedItems, setRankedItems] = useState<string[]>(() => {
    if (value && value.length > 0) {
      return value;
    }
    return question.options?.map((o) => o.value) || [];
  });

  useEffect(() => {
    if (value && value.length > 0) {
      setRankedItems(value);
    }
  }, [value]);

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= rankedItems.length) return;

    const newItems = [...rankedItems];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setRankedItems(newItems);
    onChange(newItems);
  };

  const getOptionLabel = (optionValue: string) => {
    const option = question.options?.find((o) => o.value === optionValue);
    return option ? option.label[language] : optionValue;
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        {language === 'sv'
          ? 'Dra eller använd pilarna för att rangordna (1 = högst prioritet)'
          : 'Drag or use arrows to rank (1 = highest priority)'}
      </p>

      <div className="space-y-2">
        {rankedItems.map((itemValue, index) => (
          <div
            key={itemValue}
            className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
          >
            <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-700 font-semibold rounded-full text-sm">
              {index + 1}
            </span>

            <span className="flex-1 font-medium text-gray-900">{getOptionLabel(itemValue)}</span>

            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => moveItem(index, 'up')}
                disabled={index === 0}
                className={`p-1.5 rounded ${
                  index === 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-200'
                }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => moveItem(index, 'down')}
                disabled={index === rankedItems.length - 1}
                className={`p-1.5 rounded ${
                  index === rankedItems.length - 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-200'
                }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
}
